import { getSession } from '../session.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

interface ComplianceItem {
  id: string;
  category: string;
  title: string;
  description: string;
  action?: string;
  severity: 'required' | 'recommended' | 'info';
  link?: string;
  industries?: string[];
}

const WA_COMPLIANCE: ComplianceItem[] = [
  // ── WAGES ──────────────────────────────────────────────────────────────────
  {
    id: 'wa-minwage', category: 'Wages', severity: 'required',
    title: 'Minimum Wage: $16.66/hour (2026)',
    description: 'Washington state minimum wage is $16.66/hr effective January 1, 2026. Cities may be higher: Seattle $20.76, Tukwila $21.10, Burien $19.10. No tip credit — employers must pay full minimum wage regardless of tips received.',
    action: 'Verify all employees are paid at or above the applicable minimum wage.',
    link: 'https://lni.wa.gov/workers-rights/wages/minimum-wage/',
  },
  {
    id: 'wa-overtime', category: 'Wages', severity: 'required',
    title: 'Overtime Pay — 1.5× After 40 Hours/Week',
    description: 'Non-exempt employees must receive 1.5× their regular pay rate for all hours worked over 40 in a workweek. Washington has no daily overtime requirement.',
    action: 'Track weekly hours for all non-exempt staff. Flag employees approaching 40 hours.',
  },
  {
    id: 'wa-payperiod', category: 'Wages', severity: 'required',
    title: 'Pay Period Frequency',
    description: 'Wages must be paid at least monthly, though most employers pay bi-weekly or semi-monthly. A fixed pay schedule must be established and communicated to employees.',
    action: 'Establish and post your payroll schedule.',
  },
  // ── TIPS ───────────────────────────────────────────────────────────────────
  {
    id: 'wa-tips', category: 'Tips & Gratuity', severity: 'required', industries: ['restaurant'],
    title: 'No Tip Credit — Full Minimum Wage Required',
    description: 'Washington employers cannot use tips to offset minimum wage requirements. Employees must receive at least $16.66/hr before tips.',
    action: 'Ensure base pay meets minimum wage. Tips are in addition to, not in lieu of, wages.',
  },
  {
    id: 'wa-tippool', category: 'Tips & Gratuity', severity: 'required', industries: ['restaurant'],
    title: 'Tip Pooling Rules',
    description: 'Tip pools are legal in WA. Back-of-house employees (cooks, dishwashers) may participate. However, managers, supervisors, or owners with authority to hire/fire CANNOT participate in tip pools. Mandatory service charges are taxable wages if paid to employees.',
    action: 'Exclude managers/supervisors from tip pools. Document tip distribution records for 3 years.',
  },
  {
    id: 'wa-autograt', category: 'Tips & Gratuity', severity: 'info', industries: ['restaurant'],
    title: 'Automatic Gratuity (Service Charges)',
    description: 'Mandatory service charges (e.g., 18% added automatically) are NOT tips — they belong to the employer unless distributed to employees, in which case they are wages subject to payroll taxes. Must be disclosed to customers.',
    action: 'Clearly disclose service charges on menus/receipts. Treat as wages if distributed to staff.',
  },
  // ── BREAKS ─────────────────────────────────────────────────────────────────
  {
    id: 'wa-restbreaks', category: 'Breaks & Rest', severity: 'required',
    title: 'Rest Breaks — 10 Min Per 4 Hours',
    description: 'Employees must receive a paid 10-minute rest break for every 4 hours worked (or major fraction thereof). These breaks are mandatory and may not be waived by the employee.',
    action: 'Schedule rest breaks for all shifts. Do not dock pay for these breaks.',
  },
  {
    id: 'wa-mealbreaks', category: 'Breaks & Rest', severity: 'required',
    title: 'Meal Breaks — 30 Min After 5 Hours',
    description: 'Employees who work more than 5 hours must receive a 30-minute unpaid meal break. For shifts over 11 hours, a second 30-minute meal break is required.',
    action: 'Schedule and document meal breaks. If the employee works through a meal break, it must be paid.',
  },
  // ── SICK LEAVE ─────────────────────────────────────────────────────────────
  {
    id: 'wa-sickleave', category: 'Paid Sick Leave', severity: 'required',
    title: 'Paid Sick Leave — 1 Hour per 40 Hours Worked',
    description: 'All employees (including part-time and seasonal) accrue 1 hour of paid sick leave for every 40 hours worked. Usable after 90 calendar days of employment. Unused sick leave (up to 40 hours) carries over each year.',
    action: 'Track sick leave accrual for all employees. Provide sick leave balance in pay stubs.',
    link: 'https://lni.wa.gov/workers-rights/leave/paid-sick-leave/',
  },
  // ── CONSTRUCTION-SPECIFIC ──────────────────────────────────────────────────
  {
    id: 'wa-contractor-reg', category: 'Contractor Registration', severity: 'required', industries: ['construction'],
    title: 'Contractor Registration — L&I Required',
    description: 'All contractors working in Washington must be registered with the Department of Labor & Industries (L&I). Unregistered contractors face fines and cannot legally bid on projects.',
    action: 'Verify your L&I contractor registration is current. Registration must be renewed every 2 years.',
    link: 'https://lni.wa.gov/licensing-permits/contractors/register-as-a-contractor/',
  },
  {
    id: 'wa-prevwage', category: 'Prevailing Wage', severity: 'required', industries: ['construction'],
    title: 'Prevailing Wage on Public Works Projects',
    description: 'Public works projects in WA require payment of prevailing wages, set by L&I. Applies to projects funded in whole or part by public funds. Rates vary by county and trade.',
    action: 'Check prevailing wage rates for each project county. File Intent to Pay Prevailing Wages before work begins.',
    link: 'https://lni.wa.gov/licensing-permits/public-works-projects/prevailing-wage/',
  },
  {
    id: 'wa-lien', category: 'Lien Rights', severity: 'required', industries: ['construction'],
    title: 'Lien Rights — Notice Requirements',
    description: 'Subcontractors and suppliers must provide a 60-day Notice to Owner to protect lien rights. On private projects, prime contractors must post a Notice of Commencement. Lien deadlines are strict and missing them forfeits rights.',
    action: 'Send 60-day Notice to Owner within 60 days of first furnishing labor/materials.',
  },
  {
    id: 'wa-workercomp', category: 'Workers Compensation', severity: 'required',
    title: "Workers' Compensation — L&I Coverage Required",
    description: "Washington requires workers' compensation coverage for virtually all employees through L&I (not private insurers). Rates are calculated on hours worked and job classification.",
    action: "Register with L&I and report hours worked quarterly. Penalties for non-coverage are severe.",
    link: 'https://lni.wa.gov/insurance/workers-comp/',
  },
  // ── GENERAL ────────────────────────────────────────────────────────────────
  {
    id: 'wa-harassment', category: 'Workplace Safety', severity: 'required',
    title: 'Sexual Harassment Prevention Training',
    description: 'Employers with 5+ employees must provide sexual harassment prevention training. Industry-specific training is required for restaurants/hospitality (HB 2530). Training records must be kept for 3 years.',
    action: 'Schedule annual harassment training. For restaurants, use state-approved hospitality training.',
  },
  {
    id: 'wa-postings', category: 'Required Postings', severity: 'required',
    title: 'Mandatory Workplace Postings',
    description: 'Washington requires specific posters be displayed: Minimum Wage, Job Safety & Health, Unemployment Insurance, Workers Compensation, Paid Sick Leave, PFML (Paid Family & Medical Leave), and the WLAD anti-discrimination notice.',
    action: 'Download and post all required posters from L&I website. Update annually.',
    link: 'https://lni.wa.gov/workers-rights/workplace-policies-and-environment/posters-and-notices',
  },
  {
    id: 'wa-pfml', category: 'Benefits', severity: 'required',
    title: 'Paid Family & Medical Leave (PFML)',
    description: "Employers with 50+ employees must contribute to WA's PFML program. Employees can take up to 12 weeks for family/medical leave with partial pay. Premium split: 73% employee, 27% employer (small employers exempt from employer portion).",
    action: 'Register with ESD for PFML premiums. Deduct employee portions from paychecks.',
    link: 'https://paidleave.wa.gov/',
  },
  {
    id: 'wa-i9', category: 'Hiring', severity: 'required',
    title: 'I-9 Employment Eligibility Verification',
    description: 'Federal law requires I-9 forms for all new hires within 3 days of start date. Keep completed forms for 3 years after hire or 1 year after termination (whichever is longer).',
    action: 'Complete I-9 for every new hire. Reverify employment authorization when required.',
  },
  {
    id: 'wa-scheduling', category: 'Scheduling', severity: 'recommended', industries: ['restaurant'],
    title: 'Seattle Secure Scheduling Ordinance',
    description: 'If operating in Seattle with 500+ employees (worldwide), 10 days advance notice of schedules is required. Employees must be offered additional hours before hiring new staff.',
    action: 'Post schedules 14 days in advance as best practice even outside Seattle.',
  },
];

export async function renderCompliance(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const industry = session?.industry ?? 'general';
  const businessId = session?.businessId ?? 'preview';
  const storageKey = `preuve_compliance_${businessId}`;

  const relevant = WA_COMPLIANCE.filter(
    (item) => !item.industries || item.industries.includes(industry)
  );

  const categories = [...new Set(relevant.map((i) => i.category))];

  const sevColor = { required: '#ef4444', recommended: '#f59e0b', info: '#3b82f6' };
  const sevLabel = { required: 'Required', recommended: 'Recommended', info: 'Info' };
  const sevIcon = { required: 'bi-exclamation-triangle-fill', recommended: 'bi-lightbulb-fill', info: 'bi-info-circle-fill' };

  // Load saved completion state from localStorage
  let completed: Set<string>;
  try {
    completed = new Set(JSON.parse(localStorage.getItem(storageKey) ?? '[]') as string[]);
  } catch {
    completed = new Set();
  }

  const saveCompleted = () => {
    localStorage.setItem(storageKey, JSON.stringify([...completed]));
  };

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px">
        <div>
          <h1 class="page-title"><i class="bi bi-shield-check"></i> State Compliance</h1>
          <p class="page-subtitle">Washington State labor law requirements for your business.</p>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 14px;font-size:13px;font-weight:600">
            <i class="bi bi-geo-alt-fill" style="color:var(--brand-gold)"></i> Washington State
          </div>
          <span style="background:#10b98122;color:#10b981;padding:4px 10px;border-radius:12px;font-size:11px;font-weight:700">${industry.toUpperCase()}</span>
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div class="stats-grid" style="margin-bottom:24px">
      <div class="stat-card">
        <div class="stat-value" style="color:#ef4444">${relevant.filter((i) => i.severity === 'required').length}</div>
        <div class="stat-label"><i class="bi bi-exclamation-triangle-fill"></i> Required</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color:#f59e0b">${relevant.filter((i) => i.severity === 'recommended').length}</div>
        <div class="stat-label"><i class="bi bi-lightbulb-fill"></i> Recommended</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color:#3b82f6">${relevant.filter((i) => i.severity === 'info').length}</div>
        <div class="stat-label"><i class="bi bi-info-circle-fill"></i> Informational</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color:var(--brand-gold)">$16.66</div>
        <div class="stat-label">Min Wage / Hour</div>
      </div>
    </div>

    <div style="background:var(--bg-card);border:1px solid var(--brand-gold);border-radius:var(--radius-md);padding:14px 18px;margin-bottom:24px;display:flex;align-items:flex-start;gap:12px">
      <i class="bi bi-lightbulb-fill" style="font-size:20px;color:var(--brand-gold);flex-shrink:0;margin-top:2px"></i>
      <div>
        <div style="font-size:13px;font-weight:700;color:var(--brand-gold);margin-bottom:4px">Disclaimer</div>
        <div style="font-size:12px;color:var(--text-muted);line-height:1.6">
          This compliance guide is for informational purposes only and reflects laws as of 2026.
          Always consult a licensed attorney or HR professional for advice specific to your situation.
          Laws change — verify current requirements at <a href="#" style="color:var(--brand-gold)">lni.wa.gov</a>.
        </div>
      </div>
    </div>

    <!-- Checklist by category -->
    ${categories.map((cat) => {
      const items = relevant.filter((i) => i.category === cat);
      return `
        <div class="card" style="margin-bottom:16px">
          <div style="font-size:14px;font-weight:700;margin-bottom:14px;display:flex;align-items:center;gap:8px">
            <i class="bi bi-folder2-open" style="color:var(--brand-gold)"></i> ${cat}
          </div>
          ${items.map((item) => `
            <div id="compliance-item-${item.id}" style="padding:14px;border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:10px;border-left:4px solid ${sevColor[item.severity]};transition:opacity .2s">
              <div style="display:flex;align-items:flex-start;gap:10px">
                <label style="display:flex;align-items:flex-start;gap:10px;flex:1;cursor:pointer">
                  <input type="checkbox" class="compliance-check" data-id="${item.id}"
                    ${completed.has(item.id) ? 'checked' : ''}
                    style="width:18px;height:18px;flex-shrink:0;margin-top:2px;accent-color:var(--success)" />
                  <div style="flex:1">
                    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:6px;flex-wrap:wrap">
                      <div style="font-size:13px;font-weight:700;${completed.has(item.id) ? 'text-decoration:line-through;color:var(--text-muted)' : ''}">${item.title}</div>
                      <div style="display:flex;gap:6px;align-items:center;flex-shrink:0">
                        <span style="background:${sevColor[item.severity]}22;color:${sevColor[item.severity]};padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700;white-space:nowrap">${sevLabel[item.severity]}</span>
                      </div>
                    </div>
                    <div style="font-size:12px;color:var(--text-muted);line-height:1.6;margin-bottom:8px">${item.description}</div>
                    ${item.action ? `
                      <div style="font-size:12px;color:var(--text-secondary);background:var(--bg-tertiary);padding:8px 10px;border-radius:var(--radius-sm);display:flex;gap:6px;margin-bottom:8px">
                        <i class="bi bi-arrow-right-circle-fill" style="color:var(--brand-gold);flex-shrink:0;margin-top:1px"></i>
                        <span>${item.action}</span>
                      </div>
                    ` : ''}
                    <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                      ${item.link ? `<a href="${item.link}" style="font-size:11px;color:var(--brand-blue-light);display:inline-flex;align-items:center;gap:4px"><i class="bi bi-box-arrow-up-right"></i> Official Resource</a>` : ''}
                      ${!completed.has(item.id) && item.severity === 'required' ? `
                        <button class="btn btn-ghost btn-sm ai-explain-btn" data-id="${item.id}"
                          style="font-size:11px;color:var(--brand-gold);border-color:var(--brand-gold)22">
                          <i class="bi bi-stars"></i> AI Explain Why
                        </button>
                      ` : ''}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }).join('')}

    <!-- AI Explain Modal -->
    <div id="ai-explain-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:560px;max-height:80vh;overflow-y:auto">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <h3 style="font-size:15px;font-weight:700;margin:0;display:flex;align-items:center;gap:8px">
            <i class="bi bi-stars" style="color:var(--brand-gold)"></i> AI Compliance Advisor
          </h3>
          <button class="btn btn-ghost btn-sm" id="close-ai-explain"><i class="bi bi-x-lg"></i></button>
        </div>
        <div id="ai-explain-content" style="font-size:13px;line-height:1.8;color:var(--text-secondary)">
          <div style="text-align:center;padding:20px"><div class="spinner" style="margin:0 auto"></div></div>
        </div>
      </div>
    </div>
  `;

  // Checkbox persistence
  container.querySelectorAll('.compliance-check').forEach((el) => {
    el.addEventListener('change', () => {
      const cb = el as HTMLInputElement;
      const id = cb.getAttribute('data-id')!;
      if (cb.checked) {
        completed.add(id);
      } else {
        completed.delete(id);
      }
      saveCompleted();
      // Visually strike-through
      const titleEl = el.closest('[id^="compliance-item-"]')?.querySelector('[style*="font-weight:700"]') as HTMLElement | null;
      if (titleEl) {
        titleEl.style.textDecoration = cb.checked ? 'line-through' : '';
        titleEl.style.color = cb.checked ? 'var(--text-muted)' : '';
      }
    });
  });

  // AI Explain
  document.getElementById('close-ai-explain')!.addEventListener('click', () => {
    document.getElementById('ai-explain-modal')!.style.display = 'none';
  });

  container.querySelectorAll('.ai-explain-btn').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      const item = WA_COMPLIANCE.find((i) => i.id === id);
      if (!item) return;

      document.getElementById('ai-explain-modal')!.style.display = 'flex';
      document.getElementById('ai-explain-content')!.innerHTML =
        `<div style="text-align:center;padding:20px"><div class="spinner" style="margin:0 auto"></div><div style="margin-top:12px;color:var(--text-muted);font-size:12px">Asking AI advisor…</div></div>`;

      const prompt = `You are a Washington State labor law compliance advisor for small businesses in the ${industry} industry.

A business is NOT yet in compliance with this requirement:

REQUIREMENT: ${item.title}
DESCRIPTION: ${item.description}
REQUIRED ACTION: ${item.action ?? 'N/A'}
SEVERITY: ${item.severity}

Please provide:
1. **Why this matters** — explain the real-world consequence of non-compliance (fines, lawsuits, employee harm, L&I audit, etc.)
2. **Specific risk** — what could go wrong for a ${industry} business that ignores this
3. **Step-by-step fix** — exactly what steps to take to become compliant, in plain English
4. **Timeline** — how urgently should they act and what is the penalty window
5. **Cost estimate** — rough cost to come into compliance vs. cost of non-compliance

Write in plain English, not legalese. Be specific to Washington State 2026 rules. Be direct and practical.`;

      let response = '';
      try {
        const api = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
        const result = await api.sendToAI({
          prompt,
          documentTitle: 'Compliance Explanation',
          provider: 'huggingface',
          model: 'mistral-7b',
        }) as { aiResponse: string };
        response = result.aiResponse ?? '';
      } catch {
        response = `**Why this matters:** ${item.description}\n\n**Required action:** ${item.action ?? 'Review the official guidance linked below.'}\n\n**Risk:** Non-compliance with WA State labor laws (severity: ${item.severity}) can result in L&I audits, back-pay orders, fines up to $1,000 per violation per day, and employee complaints. The Washington State Department of Labor & Industries actively enforces these requirements.\n\n**Next step:** Contact a licensed HR professional or employment attorney to address this before your next L&I audit or inspection.`;
      }

      // Convert markdown-like to HTML
      const html = response
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/^## (.+)$/gm, '<h4 style="font-size:13px;font-weight:800;color:var(--text-primary);margin:16px 0 4px">$1</h4>')
        .replace(/^# (.+)$/gm, '<h3 style="font-size:15px;font-weight:800;color:var(--brand-gold);margin:0 0 12px">$1</h3>')
        .replace(/^\d+\. (.+)$/gm, '<div style="margin:4px 0;padding-left:12px"><span style="color:var(--brand-gold)">→</span> $1</div>')
        .replace(/\n\n/g, '<br><br>')
        .replace(/\n/g, '<br>');

      document.getElementById('ai-explain-content')!.innerHTML = `
        <div style="background:var(--bg-tertiary);border-radius:var(--radius-sm);padding:12px;margin-bottom:16px">
          <div style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Requirement</div>
          <div style="font-size:13px;font-weight:700">${item.title}</div>
        </div>
        ${html}
      `;
    });
  });
}
